import { useState } from 'react';
import { NavLink } from 'react-router-dom';
// mantine
import { useDisclosure } from '@mantine/hooks';
import { ActionIcon, Loader, Menu, Table } from '@mantine/core';
// components
import { Plus, EllipsisVertical, Pencil, Trash2 } from 'lucide-react';
import { TLibrary } from '@newo/api/gateway';
import Drawer from '@/components/Drawer';
import LibraryForm from '@/pages/Libraries/components/LibraryForm';
import NoRecords from '@/components/NoRecords';
// icons
// utils
import { PATHS } from '@/const';
import {
    useLibrariesQuery,
    useDeleteLibraryMutation
} from '@/services/designer/libraries/libraries.queries';
import { openConfirmDeleteModal } from '@/utils/ui';

interface LibrariesProps {
    projectId?: string;
}

const Libraries = ({ projectId }: LibrariesProps) => {
    const [selectedLibrary, setSelectedLibrary] = useState<
        TLibrary | undefined
    >();
    const [isDrawerOpened, handlers] = useDisclosure(false);

    const {
        data: libraries,
        isLoading,
        isSuccess,
        refetch
    } = useLibrariesQuery(projectId);
    const { mutate: deleteLibrary } = useDeleteLibraryMutation();

    const handleLibraryDrawerOpen = (library?: TLibrary) => {
        setSelectedLibrary(library);
        handlers.open();
    };

    const handleLibraryDrawerClose = () => {
        setSelectedLibrary(undefined);
        handlers.close();
    };

    const handleDeleteLibrary = (library: TLibrary) => {
        openConfirmDeleteModal({
            title: `Delete ${library.idn} library?`,
            children: `Are you sure you want to delete ${library.idn} library?`,
            confirmLabel: 'Delete Library',
            onConfirm: () => {
                if (!projectId) {
                    return;
                }
                deleteLibrary(
                    { projectId, libraryId: library.id },
                    {
                        onSuccess: () => {
                            refetch();
                        }
                    }
                );
            }
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-60">
                <Loader />
            </div>
        );
    }

    return (
        <>
            <div className="flex gap-4 justify-end py-4">
                <Menu>
                    <Menu.Target>
                        <ActionIcon variant="transparent">
                            <EllipsisVertical />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<Plus />}
                            onClick={() => {
                                handleLibraryDrawerOpen();
                            }}
                        >
                            Create Library
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </div>
            <div className="rounded-lg border border-solid border-gray-3">
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Idn</Table.Th>
                            <Table.Th className="text-right">Actions</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody className="[&>tr:last-child]:border-b-0!">
                        {isSuccess && libraries.length === 0 ? (
                            <Table.Tr>
                                <Table.Td colSpan={2}>
                                    <NoRecords />
                                </Table.Td>
                            </Table.Tr>
                        ) : undefined}
                        {libraries?.map((library) => (
                            <Table.Tr className="rowFadeIn" key={library.id}>
                                <Table.Td>
                                    <NavLink
                                        className="font-medium break-all text-sm text-black"
                                        to={`${PATHS.projects}/${projectId}/libraries/${library.id}/builder`}
                                    >
                                        {library.idn}
                                    </NavLink>
                                </Table.Td>
                                <Table.Td className="text-right">
                                    <div className="flex justify-end gap-4">
                                        <ActionIcon
                                            color="red"
                                            size="xs"
                                            variant="transparent"
                                            onClick={() => {
                                                handleDeleteLibrary(library);
                                            }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </ActionIcon>
                                        <ActionIcon
                                            size="xs"
                                            variant="transparent"
                                            onClick={() => {
                                                handleLibraryDrawerOpen(
                                                    library
                                                );
                                            }}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </ActionIcon>
                                    </div>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </div>
            <Drawer
                withOverlay
                name="library-form"
                opened={isDrawerOpened}
                position="right"
                size="sm"
                title={
                    selectedLibrary
                        ? `Edit ${selectedLibrary.idn} library`
                        : 'Create Library'
                }
                onClose={handleLibraryDrawerClose}
            >
                <LibraryForm
                    library={selectedLibrary}
                    projectId={projectId ?? ''}
                    onClose={handleLibraryDrawerClose}
                    onSubmit={refetch}
                />
            </Drawer>
        </>
    );
};

export default Libraries;
